import { useMutation } from '@apollo/client/react';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { toaster } from '#components/ui/toaster';
import { UPSERT_DOCUMENT } from '#graphql/mutations/upsert-document';
import { useAppConfig } from '#providers/app-config-provider';
import { documentViewHref } from '#utils/document-app-path';

import { useDocumentContext } from '../document-context';
import { refetchInTransition } from '../refetch-utils';

export function useRenameDocument() {
  const navigate = useNavigate();
  const { document, displayPath, getSerializedDocument, refetch } =
    useDocumentContext();
  const { removeRecentlyOpenedDocumentById } = useAppConfig();
  const [upsertDocument] = useMutation(UPSERT_DOCUMENT);

  const documentPathForRename =
    document?.__typename === 'Document' ? document.path : displayPath;

  const handleRename = useCallback(
    async (newPath: string, currentValue: string) => {
      if (document?.__typename !== 'Document') return;

      try {
        await upsertDocument({
          variables: {
            input: {
              id: document.id,
              path: newPath,
              value: currentValue,
            },
          },
        });
        toaster.create({
          title: 'Success',
          description: 'Document renamed.',
        });
        removeRecentlyOpenedDocumentById(document.id);
        await refetchInTransition(refetch);
        navigate(documentViewHref(newPath), { replace: true });
      } catch (error) {
        toaster.create({
          title: 'Error',
          description: error instanceof Error ? error.message : 'Unknown error',
        });
      }
    },
    [
      document,
      navigate,
      refetch,
      removeRecentlyOpenedDocumentById,
      upsertDocument,
    ],
  );

  return {
    documentPathForRename,
    getSerializedDocument,
    handleRename,
  };
}
